import { Box, Chip, Link } from '@mui/material'
import { MAX_WIDGET_SIZE } from '@shared/constants'
import { i18WithDefault as t } from '@shared/locale'
import { HeadCell, ROWS_PER_PAGE_OPTIONS, Row, SharedTable, Spacer2, InvoiceStatus } from 'components-ui'
import { useEffect, useMemo, useState } from 'react'
import { Link as NavLink, useParams } from 'react-router-dom'
import { Hooks } from 'service-api'
import { TBookingInvoiceSearchOptions, TCustomInvoiceDto } from 'service-api/src/types/general'

const PURCHASE_DATE = 'updatedAt',
  SERVICE = 'service',
  PETS = 'pets',
  STATUS = 'status',
  SERVICE_DATES = 'serviceDates',
  ACTIONS = 'actions'

const headCells: HeadCell[] = [
  { id: SERVICE_DATES, label: t('global.label.visitDates'), sortable: true },
  { id: SERVICE, label: t('global.label.visit') },
  { id: PETS, label: t('global.label.pets') },
  { id: STATUS, label: t('global.label.status') },
  { id: PURCHASE_DATE, label: t('global.label.purchaseDate'), sortable: true },
  { id: ACTIONS, label: t('global.label.actions') },
]

const getSearchOptions = (options: TBookingInvoiceSearchOptions) => {
  if (options.sortColumn === SERVICE_DATES) {
    return {
      ...options,
      includes: ['order.pets.locationPetProfile', 'items.petRelations'],
    }
  } else {
    return {
      ...options,
      includes: [
        'invoices.requestPeriods',
        'invoices.locationServiceType.serviceType',
        'pets.locationPetProfile',
        'invoices.items.petRelations',
      ],
    }
  }
}
export const PurchaseHistory = () => {
  const { accountName = '' } = useParams()
  const [invoices, setInvoices] = useState<TCustomInvoiceDto[]>([])
  const [requestOptions, setRequestOptions] = useState<TBookingInvoiceSearchOptions>({
    limit: ROWS_PER_PAGE_OPTIONS[0],
    sortColumn: PURCHASE_DATE,
    sortOrder: 'desc',
  })

  const { data, isLoading } = Hooks.useClientBookingInvoices(accountName, getSearchOptions(requestOptions))

  useEffect(() => {
    if (data?.results) {
      setInvoices((preInvoices) => [...preInvoices, ...data.results])
    }
  }, [data])

  useEffect(() => {
    document.title = t('profile.bookings.title')
  }, [])

  const rows: Row[] = useMemo(() => {
    return invoices.map((invoice) => ({
      [SERVICE_DATES]: invoice.serviceDate,
      [SERVICE]: invoice.service ? <Chip label={invoice.service} /> : null,
      [PETS]: invoice.pet?.displayName,
      [STATUS]: <InvoiceStatus invoice={invoice} />,
      [PURCHASE_DATE]: invoice.purchaseDate,
      [ACTIONS]: (
        <Link
          component={NavLink}
          to={`/${accountName}/account/bookings/${invoice.orderId}`}
          variant="button"
          color="primary"
          underline="none"
        >
          {t('purchaseHistory.viewSummary')}
        </Link>
      ),
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices])

  return (
    <Box sx={{ maxWidth: MAX_WIDGET_SIZE, pb: '130px', margin: '0 auto' }}>
      <Spacer2 />
      <SharedTable
        rows={rows}
        head={headCells}
        defaultOrder="desc"
        defaultOrderBy={PURCHASE_DATE}
        isLoading={isLoading}
        emptyTableMessage={t('purchaseHistory.emptyTable')}
        hasMore={!!data?.nextToken}
        loadMore={(limit) => {
          if (data?.nextToken) {
            setRequestOptions((prevOptions) => ({ ...prevOptions, limit, nextToken: data.nextToken! }))
          }
        }}
        onSort={(sortColumn, sortOrder, limit) => {
          setInvoices([])
          setRequestOptions((prevOptions) => ({ ...prevOptions, sortColumn, sortOrder, limit, nextToken: undefined }))
        }}
      />
    </Box>
  )
}
